import type * as Monaco from "monaco-editor";
import {
  parseAndResolve,
  inferResultSignature,
  parseFormula,
  findNodeAtOffset,
  nodeRange,
  evaluateNode,
  getDisplaySymbol,
  CURVE_FORMULA_FIELDS,
} from "@brewbase/lib";
import type { EvalValue, NodeEvaluation } from "@brewbase/lib";
import type { FormulaContextSpec } from "./context";
import { CURVE_FIELD_LABELS } from "./curve-fields";

/** Variable scope a model is evaluated against for live values, keyed by URI. */
export type FormulaEvalScope = Map<string, EvalValue>;

export const LANGUAGE_ID = "brewbase-formula";
export const THEME_ID = "brewbase-formula-light";

export const FORMULA_LANGUAGE_CONFIGURATION: Monaco.languages.LanguageConfiguration = {
  brackets: [["(", ")"]],
};

/**
 * Bracket pairs coloured by nesting depth. Monaco's light defaults are blue,
 * green and brown, which blur into the blue numbers and green identifiers.
 */
export const BRACKET_PAIR_COLORS: Monaco.editor.IColors = {
  "editorBracketHighlight.foreground1": "#be185d",
  "editorBracketHighlight.foreground2": "#b45309",
  "editorBracketHighlight.foreground3": "#6d28d9",
  "editorBracketHighlight.unexpectedBracket.foreground": "#dc2626",
};

/**
 * One-context-per-model registry. Each editor binds its model URI to its
 * current FormulaContextSpec; the shared providers look it up from here.
 * Avoids registering N duplicate completion providers when N editors are
 * mounted on the same page.
 */
const contextByUri = new Map<string, FormulaContextSpec>();

/** Signals the inlay-hints provider to re-query (e.g. when context first binds). */
let fireInlayHintsChanged: (() => void) | null = null;

export function setModelContext(uri: string, ctx: FormulaContextSpec | null) {
  if (ctx) contextByUri.set(uri, ctx);
  else contextByUri.delete(uri);
  // The result-type inlay depends on the context, which binds after the model
  // mounts — nudge Monaco to re-request once it's available.
  fireInlayHintsChanged?.();
}

function getContext(uri: string): FormulaContextSpec | null {
  return contextByUri.get(uri) ?? null;
}

/**
 * Live-value scope per model URI. When set, the inlay shows the computed result
 * and hovering a sub-expression shows its current value. Mirrors contextByUri
 * so the shared providers stay stateless.
 */
const scopeByUri = new Map<string, FormulaEvalScope>();

export function setModelScope(uri: string, scope: FormulaEvalScope | null) {
  if (scope) scopeByUri.set(uri, scope);
  else scopeByUri.delete(uri);
  // The result inlay reads the scope — re-query once it (re)binds.
  fireInlayHintsChanged?.();
}

function getScope(uri: string): FormulaEvalScope | null {
  return scopeByUri.get(uri) ?? null;
}

/**
 * Models whose context/value scope is still being fetched. Drives a `→ …`
 * loading hint so the inlay isn't blank while the async data lands.
 */
const loadingByUri = new Set<string>();

export function setModelLoading(uri: string, loading: boolean) {
  if (loading) loadingByUri.add(uri);
  else loadingByUri.delete(uri);
  fireInlayHintsChanged?.();
}

function isLoading(uri: string): boolean {
  return loadingByUri.has(uri);
}

/** Columns of a local TABULAR_DATA attribute, if `code` names one. */
function tabularLocalColumns(
  ctx: FormulaContextSpec,
  code: string,
): FormulaContextSpec["scopes"]["local"] | undefined {
  const local = ctx.scopes.local.find((r) => r.code === code);
  if (local?.kind === "TABULAR_DATA" && local.tabularColumns?.length) {
    return local.tabularColumns;
  }
  return undefined;
}

/** Properties of a local INVENTORY_ITEM_REFERENCE attribute, if `code` names one. */
function referenceLocalProps(
  ctx: FormulaContextSpec,
  code: string,
): FormulaContextSpec["scopes"]["local"] | undefined {
  const local = ctx.scopes.local.find((r) => r.code === code);
  if (
    local?.kind === "INVENTORY_ITEM_REFERENCE" &&
    local.referenceProperties?.length
  ) {
    return local.referenceProperties;
  }
  return undefined;
}

/** Whether a function returns a row (object) or a table — i.e. supports `.col`. */
function returnsRowOrTable(ctx: FormulaContextSpec, name: string): boolean {
  return ctx.functions.some(
    (f) =>
      f.name === name &&
      (f.returnType === "object" || f.returnType === "table"),
  );
}

/**
 * The call enclosing the cursor: the function name, its first-argument
 * identifier (the table), and which argument index the cursor sits in. Scans
 * backwards honouring nesting; returns null when not inside a call.
 */
function enclosingCall(
  before: string,
): { name: string; arg0?: string; argIndex: number } | null {
  let depth = 0;
  let commas = 0;
  let i = before.length - 1;
  for (; i >= 0; i--) {
    const ch = before[i];
    if (ch === ")") depth++;
    else if (ch === "(") {
      if (depth === 0) break;
      depth--;
    } else if (ch === "," && depth === 0) commas++;
  }
  if (i < 0) return null;
  const nameMatch = before.slice(0, i).match(/([a-zA-Z_]\w*)\s*$/);
  if (!nameMatch) return null;
  const arg0Match = before.slice(i + 1).match(/^\s*([a-zA-Z_]\w*)/);
  return { name: nameMatch[1], arg0: arg0Match?.[1], argIndex: commas };
}

/** Declared parameter type at an argument index (honours a variadic tail). */
function paramTypeAt(
  ctx: FormulaContextSpec,
  name: string,
  argIndex: number,
): string | undefined {
  for (const fn of ctx.functions) {
    if (fn.name !== name) continue;
    const params = fn.parameters;
    const last = params[params.length - 1];
    const p =
      argIndex < params.length
        ? params[argIndex]
        : last?.variadic
          ? last
          : undefined;
    if (p) return typeof p.type === "string" ? p.type : p.type[0];
  }
  return undefined;
}

let registered = false;

export function registerFormulaLanguage(monaco: typeof Monaco): void {
  if (registered) return;
  registered = true;

  monaco.languages.register({ id: LANGUAGE_ID });
  // Declaring the pair is what turns on Monaco's per-depth bracket colours
  // and the matching-bracket highlight at the cursor.
  monaco.languages.setLanguageConfiguration(LANGUAGE_ID, FORMULA_LANGUAGE_CONFIGURATION);

  monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    tokenizer: {
      root: [
        // Backtick-quoted identifiers: recognised so legacy formulas render
        // correctly, but marked as deprecated — new completions never emit them.
        ["`", { token: "identifier.deprecated", next: "@backtick" }],
        [/"[^"\n]*"/, "string"],
        [/\d+(?:\.\d+)?/, "number"],
        [/\bupstream\b/, "type"],
        [/\brow\b/, "type"],
        [/\bprev\b/, "type"],
        [/\belapsed\b/, "type"],
        [/\b(and|or|not)\b/, "keyword"],
        [/\b[a-zA-Z_][a-zA-Z0-9_]*(?=\s*\()/, "keyword"],
        [/\b[a-zA-Z_][a-zA-Z0-9_]*\b/, "identifier"],
        [/\./, "delimiter"],
        [/,/, "delimiter"],
        [/\(/, "delimiter.parenthesis"],
        [/\)/, "delimiter.parenthesis"],
        [/==|!=|>=|<=|>|</, "operator"],
        [/[+\-*/]/, "operator"],
        [/\s+/, "white"],
      ],
      backtick: [
        [/[^`]+/, "identifier.deprecated"],
        ["`", { token: "identifier.deprecated", next: "@pop" }],
      ],
    },
  });

  monaco.editor.defineTheme(THEME_ID, {
    base: "vs",
    inherit: true,
    rules: [
      { token: "number", foreground: "1d4ed8" },
      { token: "identifier", foreground: "166534" },
      { token: "identifier.deprecated", foreground: "a16207", fontStyle: "italic" },
      { token: "type", foreground: "7c3aed" },
      { token: "keyword", foreground: "c2410c" },
      { token: "operator", foreground: "525252" },
      { token: "delimiter", foreground: "737373" },
    ],
    colors: BRACKET_PAIR_COLORS,
  });

  registerCompletionProvider(monaco);
  registerHoverProvider(monaco);
  registerValueHoverProvider(monaco);
  registerSignatureHelpProvider(monaco);
  registerInlayHintsProvider(monaco);
}

// ---------------------------------------------------------------------------
// Live values — formatting shared by the inlay and the value hover.
// ---------------------------------------------------------------------------

/** Compact display for a computed number (trims float noise, keeps precision). */
function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  return Number(value.toPrecision(6)).toString();
}

/** A number's value+unit for the inlay (`42.3 g/L`), or null for non-numbers. */
function inlayValueLabel(ev: NodeEvaluation): string | null {
  if (ev.kind !== "number") return null;
  return ev.unit ? `${formatNumber(ev.value)} ${getDisplaySymbol(ev.unit)}` : formatNumber(ev.value);
}

/** Markdown lines describing an evaluated sub-expression, for the hover card. */
function hoverValueContents(
  ev: NodeEvaluation,
): { value: string }[] | null {
  switch (ev.kind) {
    case "number":
      return [
        {
          value: ev.unit
            ? `**${formatNumber(ev.value)}** ${getDisplaySymbol(ev.unit)}`
            : `**${formatNumber(ev.value)}**`,
        },
      ];
    case "time":
      return [{ value: `**${new Date(ev.value * 1000).toLocaleString()}**` }, { value: "*timestamp*" }];
    case "boolean":
      return [{ value: `**${ev.value ? "true" : "false"}**` }];
    case "list":
      return [{ value: `*list — ${ev.length} value${ev.length === 1 ? "" : "s"}*` }];
    case "row":
      return [{ value: "*row*" }];
    case "table":
      return [{ value: "*table*" }];
    case "missing":
      return [
        {
          value: ev.blankRefs.length
            ? `*no value — ${ev.blankRefs.map((r) => `\`${r}\``).join(", ")} blank*`
            : "*no value — an input is blank*",
        },
      ];
    case "error":
      return [{ value: `⚠ ${ev.message}` }];
  }
}

// ---------------------------------------------------------------------------
// Inlay hints — the inferred result type/unit, shown as ghost text at the end
// of the formula (e.g. `permeate / hours(elapsed)  → L/h`).
// ---------------------------------------------------------------------------

/** A single result inlay at the end of the formula. */
function inlayHint(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  text: string,
): Monaco.languages.InlayHintList {
  const line = model.getLineCount();
  const column = model.getLineMaxColumn(line);
  return {
    hints: [
      {
        position: { lineNumber: line, column },
        label: text,
        kind: monaco.languages.InlayHintKind.Type,
        paddingLeft: true,
      },
    ],
    dispose() {},
  };
}

const loadingHint = (monaco: typeof Monaco, model: Monaco.editor.ITextModel) =>
  inlayHint(monaco, model, "→ …");

function registerInlayHintsProvider(monaco: typeof Monaco) {
  const emitter = new monaco.Emitter<void>();
  fireInlayHintsChanged = () => emitter.fire();

  monaco.languages.registerInlayHintsProvider(LANGUAGE_ID, {
    onDidChangeInlayHints: emitter.event,
    provideInlayHints(model) {
      const empty = { hints: [], dispose() {} };
      // The whole body is guarded: evaluating against placeholder/real data can
      // throw (a function's execute() may throw rather than return an EvalError).
      // A synchronous throw here makes Monaco drop the hint and not retry until
      // the next edit, so the result inlay would vanish "permanently".
      try {
        const uri = model.uri.toString();
        const value = model.getValue();
        if (!value.trim()) return empty;

        const loading = isLoading(uri);
        const ctx = getContext(uri);
        // Context still being fetched and nothing to show yet → loading hint.
        if (!ctx) return loading ? loadingHint(monaco, model) : empty;

        // A bound scope yields the computed result (`→ 42.3 g/L`); otherwise fall
        // back to the inferred type (`→ g/L`). `hasValue` distinguishes a real
        // result from a type guess, so a still-fetching scope shows `… `.
        let valueLabel: string | null = null;
        const scope = getScope(uri);
        if (scope) {
          valueLabel = inlayValueLabel(evaluateNode(parseFormula(value).ast, scope));
        }
        const label = valueLabel ?? inferResultSignature(value, ctx)?.label ?? null;
        if (!label) return loading ? loadingHint(monaco, model) : empty;

        // Type shown but the value scope is still loading → trail an ellipsis.
        const text = loading && !valueLabel ? `→ ${label} …` : `→ ${label}`;
        return inlayHint(monaco, model, text);
      } catch {
        return empty;
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Completion — context-driven, never emits backticks
// ---------------------------------------------------------------------------

function registerCompletionProvider(monaco: typeof Monaco) {
  monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: [".", "(", ","],
    provideCompletionItems(model, position) {
      const ctx = getContext(model.uri.toString());
      if (!ctx) return { suggestions: [] };

      const offset = model.getOffsetAt(position);
      const text = model.getValue();
      const beforeCursor = text.slice(0, offset);

      // `samples.` → sample abbreviations.
      if (/(?:^|[^.\w])samples\.$/.test(beforeCursor) && ctx.scopes.samples) {
        return {
          suggestions: ctx.scopes.samples.map((s) => ({
            label: s.abbreviation,
            detail: `Sample: ${s.name}`,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: `${s.abbreviation}.`,
            range: wordRange(monaco, model, position),
            command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
          })),
        };
      }

      // `samples.<abbr>.` → attribute codes. The runtime addresses sample
      // attributes flat under the abbreviation (no test level), so list every
      // attribute code across the sample's tests, deduped, with the test name as
      // detail. `samples.<abbr>.<code>` is the full path.
      const sampleAbbrMatch = beforeCursor.match(
        /(?:^|[^.\w])samples\.([a-zA-Z_][a-zA-Z0-9_]*)\.$/,
      );
      if (sampleAbbrMatch && ctx.scopes.samples) {
        const sample = ctx.scopes.samples.find(
          (s) => s.abbreviation === sampleAbbrMatch[1],
        );
        if (sample) {
          const seen = new Set<string>();
          const suggestions: Monaco.languages.CompletionItem[] = [];
          for (const test of sample.tests) {
            for (const attr of test.attrs) {
              if (seen.has(attr.code)) continue;
              seen.add(attr.code);
              suggestions.push({
                label: attr.code,
                detail: attr.name
                  ? `${attr.name} · ${test.name}`
                  : test.name,
                kind: monaco.languages.CompletionItemKind.Field,
                insertText: attr.code,
                range: wordRange(monaco, model, position),
              });
            }
          }
          return { suggestions };
        }
      }

      // `curves.` → the template's compounds; `curves.<compound>.` → its numbers.
      if (/(?:^|[^.\w])curves\.$/.test(beforeCursor) && ctx.scopes.curves) {
        return {
          suggestions: ctx.scopes.curves.map((c) => ({
            label: c.code,
            detail: `Calibration curve: ${c.name}`,
            documentation: c.sourceDescription,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: `${c.code}.`,
            range: wordRange(monaco, model, position),
            command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
          })),
        };
      }
      const curveMatch = beforeCursor.match(
        /(?:^|[^.\w])curves\.([a-zA-Z_][a-zA-Z0-9_]*)\.$/,
      );
      if (curveMatch && ctx.scopes.curves) {
        const curve = ctx.scopes.curves.find((c) => c.code === curveMatch[1]);
        if (curve) {
          return {
            suggestions: CURVE_FORMULA_FIELDS.map((field) => ({
              label: field,
              detail: `${CURVE_FIELD_LABELS[field]} · ${curve.name}`,
              documentation: curve.sourceDescription,
              kind: monaco.languages.CompletionItemKind.Field,
              insertText: field,
              range: wordRange(monaco, model, position),
            })),
          };
        }
      }

      // `fn(table).` — member access on a row/table-returning function result.
      const memberMatch = beforeCursor.match(
        /([a-zA-Z_]\w*)\(\s*([a-zA-Z_]\w*)\b[^()]*\)\.$/,
      );
      if (memberMatch && returnsRowOrTable(ctx, memberMatch[1])) {
        const cols = tabularLocalColumns(ctx, memberMatch[2]);
        if (cols) {
          return {
            suggestions: cols.map((r) =>
              identifierSuggestion(monaco, r, offset, model, position),
            ),
          };
        }
      }

      // Inside a `predicate`/`column_ref` argument — `find(table, <here>`,
      // `maxrow(table, <here>` — offer the table's columns (bare column scope)
      // alongside the normal palette. Restricted to those slot types, so an
      // index arg (`get`/`nth`) or string arg (`resample`) is NOT mistaken for a
      // column position.
      const call = enclosingCall(beforeCursor);
      if (call && call.argIndex >= 1 && call.arg0) {
        const ptype = paramTypeAt(ctx, call.name, call.argIndex);
        if (ptype === "predicate" || ptype === "column_ref") {
          const cols = tabularLocalColumns(ctx, call.arg0);
          if (cols) {
            return {
              suggestions: [
                ...cols.map((r) =>
                  identifierSuggestion(monaco, r, offset, model, position),
                ),
                ...topLevelSuggestions(monaco, ctx, offset, model, position),
              ],
            };
          }
        }
      }

      // `<scope>.` where scope is sample abbr, upstream, row, or a tabular local
      const scopeMatch = beforeCursor.match(/([a-zA-Z_][a-zA-Z0-9_]*)\.$/);
      if (scopeMatch) {
        const scope = scopeMatch[1];
        const localCols = tabularLocalColumns(ctx, scope);
        if (localCols) {
          return {
            suggestions: localCols.map((r) =>
              identifierSuggestion(monaco, r, offset, model, position),
            ),
          };
        }
        const refProps = referenceLocalProps(ctx, scope);
        if (refProps) {
          return {
            suggestions: refProps.map((r) =>
              identifierSuggestion(monaco, r, offset, model, position),
            ),
          };
        }
        if (scope === "upstream" && ctx.scopes.upstream) {
          return {
            suggestions: ctx.scopes.upstream.map((r) =>
              identifierSuggestion(monaco, r, offset, model, position),
            ),
          };
        }
        if ((scope === "row" || scope === "prev") && ctx.scopes.tabular) {
          return {
            suggestions: ctx.scopes.tabular.columns.map((r) =>
              identifierSuggestion(monaco, r, offset, model, position),
            ),
          };
        }
      }

      // Top-level: local attrs + upstream/row/sample roots + functions
      return {
        suggestions: topLevelSuggestions(monaco, ctx, offset, model, position),
      };
    },
  });
}

/** The default suggestion set: locals, upstream/row/sample roots, functions. */
function topLevelSuggestions(
  monaco: typeof Monaco,
  ctx: FormulaContextSpec,
  offset: number,
  model: Monaco.editor.ITextModel,
  position: Monaco.Position,
): Monaco.languages.CompletionItem[] {
  const suggestions: Monaco.languages.CompletionItem[] = [];
  for (const attr of ctx.scopes.local) {
    suggestions.push(identifierSuggestion(monaco, attr, offset, model, position));
  }
  if (ctx.scopes.upstream) {
    suggestions.push({
      label: "upstream",
      detail: "Upstream operation",
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: "upstream.",
      range: wordRange(monaco, model, position),
      command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
    });
  }
  if (ctx.scopes.tabular) {
    suggestions.push({
      label: "row",
      detail: "Same-row column",
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: "row.",
      range: wordRange(monaco, model, position),
      command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
    });
    suggestions.push({
      label: "prev",
      detail: "Previous-row column",
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: "prev.",
      range: wordRange(monaco, model, position),
      command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
    });
    if (ctx.scopes.tabular.index) {
      suggestions.push({
        label: "elapsed",
        detail: "Time since previous row",
        documentation:
          "Duration between this row's index value and the previous row's. " +
          "Blank on the first row. Wrap in hours()/minutes() to set the unit, " +
          "e.g. `permeate / area / hours(elapsed)`.",
        kind: monaco.languages.CompletionItemKind.Variable,
        insertText: "elapsed",
        range: wordRange(monaco, model, position),
      });
    }
  }
  if (ctx.scopes.curves && ctx.scopes.curves.length > 0) {
    suggestions.push({
      label: "curves",
      detail: "Calibration curves",
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: "curves.",
      range: wordRange(monaco, model, position),
      command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
    });
  }
  if (ctx.scopes.samples && ctx.scopes.samples.length > 0) {
    suggestions.push({
      label: "samples",
      detail: "Sample test results",
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: "samples.",
      range: wordRange(monaco, model, position),
      command: { id: "editor.action.triggerSuggest", title: "Trigger suggest" },
    });
  }
  for (const fn of ctx.functions) {
    suggestions.push({
      label: fn.name,
      detail: fn.category ? `${fn.category} · ${fn.returnType}` : fn.returnType,
      documentation: functionDocumentation(fn),
      kind: monaco.languages.CompletionItemKind.Function,
      insertText: `${fn.name}($0)`,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range: wordRange(monaco, model, position),
    });
  }
  return suggestions;
}

function identifierSuggestion(
  monaco: typeof Monaco,
  ref: { code: string; name: string; kind?: string; unit?: string; sourceDescription?: string },
  _offset: number,
  model: Monaco.editor.ITextModel,
  position: Monaco.Position,
): Monaco.languages.CompletionItem {
  const parts: string[] = [];
  if (ref.kind) parts.push(ref.kind);
  if (ref.unit) parts.push(ref.unit);
  return {
    label: ref.code,
    detail: parts.join(" · ") || ref.name,
    documentation: ref.sourceDescription ?? ref.name,
    kind: monaco.languages.CompletionItemKind.Field,
    insertText: ref.code,
    range: wordRange(monaco, model, position),
  };
}

function wordRange(
  _monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  position: Monaco.Position,
): Monaco.IRange {
  const word = model.getWordUntilPosition(position);
  return {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: word.startColumn,
    endColumn: word.endColumn,
  };
}

// ---------------------------------------------------------------------------
// Hover — identifier + function docs
// ---------------------------------------------------------------------------

function registerHoverProvider(monaco: typeof Monaco) {
  monaco.languages.registerHoverProvider(LANGUAGE_ID, {
    provideHover(model, position) {
      const ctx = getContext(model.uri.toString());
      if (!ctx) return null;
      const word = model.getWordAtPosition(position);
      if (!word) return null;
      const line = model.getLineContent(position.lineNumber);
      const before = line.slice(0, word.startColumn - 1);

      const fnSig = ctx.functions.find((f) => f.name === word.word);
      if (fnSig && line.charAt(word.endColumn - 1) === "(") {
        return {
          contents: [
            { value: `**${fnSig.name}**(${fnSig.parameters.map((p) => p.name).join(", ")}) → ${fnSig.returnType}` },
            { value: fnSig.description },
            ...(fnSig.examples?.length
              ? [{ value: "```\n" + fnSig.examples.join("\n") + "\n```" }]
              : []),
          ],
        };
      }

      // `fn(table).<col>` — column read off a row/table-returning function.
      const memberBefore = before.match(
        /([a-zA-Z_]\w*)\(\s*([a-zA-Z_]\w*)\b[^()]*\)\.$/,
      );
      if (memberBefore && returnsRowOrTable(ctx, memberBefore[1])) {
        const ref = tabularLocalColumns(ctx, memberBefore[2])?.find(
          (r) => r.code === word.word,
        );
        if (ref) return identifierHover(ref, "Column");
      }

      const scopeMatch = before.match(/([a-zA-Z_][a-zA-Z0-9_]*)\.([a-zA-Z_][a-zA-Z0-9_]*\.)?$/);
      if (scopeMatch) {
        const root = scopeMatch[1];
        const localCol = tabularLocalColumns(ctx, root)?.find(
          (r) => r.code === word.word,
        );
        if (localCol) return identifierHover(localCol, "Column");
        const refProp = referenceLocalProps(ctx, root)?.find(
          (r) => r.code === word.word,
        );
        if (refProp) return identifierHover(refProp, "Property");
        if (root === "upstream" && ctx.scopes.upstream) {
          const ref = ctx.scopes.upstream.find((r) => r.code === word.word);
          if (ref) return identifierHover(ref, "Upstream");
        }
        if ((root === "row" || root === "prev") && ctx.scopes.tabular) {
          const ref = ctx.scopes.tabular.columns.find((r) => r.code === word.word);
          if (ref) return identifierHover(ref, root === "prev" ? "Previous row" : "Row column");
        }
        // `curves.<compound>` and `curves.<compound>.<field>`, with the version in use.
        if (root === "curves" && ctx.scopes.curves) {
          const code = scopeMatch[2] ? scopeMatch[2].slice(0, -1) : word.word;
          const curve = ctx.scopes.curves.find((c) => c.code === code);
          const field = scopeMatch[2]
            ? CURVE_FORMULA_FIELDS.find((f) => f === word.word)
            : undefined;
          if (curve && (!scopeMatch[2] || field)) {
            return {
              contents: [
                { value: `**${curve.name}**${field ? ` · ${CURVE_FIELD_LABELS[field]}` : ""}` },
                { value: "*Calibration curve*" },
                ...(curve.sourceDescription ? [{ value: curve.sourceDescription }] : []),
                { value: "A test converts with the version in use when it first converts, and keeps it." },
              ],
            };
          }
        }
        // `samples.<abbr>.<code>` — hovering the attribute code. Codes are flat
        // under the sample, so search across its tests for the matching code.
        if (root === "samples" && scopeMatch[2]) {
          const abbr = scopeMatch[2].slice(0, -1);
          const sample = ctx.scopes.samples?.find((s) => s.abbreviation === abbr);
          if (sample) {
            for (const test of sample.tests) {
              const attr = test.attrs.find((a) => a.code === word.word);
              if (attr) return identifierHover(attr, `${sample.name} → ${test.name}`);
            }
          }
        }
      }

      if (word.word === "elapsed" && ctx.scopes.tabular?.index) {
        const idx = ctx.scopes.tabular.index;
        return {
          contents: [
            { value: "**elapsed**" },
            {
              value: `*Time since previous row* — duration from the previous row's \`${idx.name}\` to this one's${idx.unit ? ` · ${idx.unit}` : ""}`,
            },
            {
              value:
                "Blank on the first row. Wrap in hours()/minutes() to set the unit.",
            },
          ],
        };
      }

      // A bare column inside a predicate / column_ref argument
      // (`find(measurements, temp > 30)` → hovering `temp`).
      const call = enclosingCall(before);
      if (call && call.argIndex >= 1 && call.arg0) {
        const ptype = paramTypeAt(ctx, call.name, call.argIndex);
        if (ptype === "predicate" || ptype === "column_ref") {
          const col = tabularLocalColumns(ctx, call.arg0)?.find(
            (c) => c.code === word.word,
          );
          if (col) return identifierHover(col, "Column");
        }
      }

      const localRef = ctx.scopes.local.find((r) => r.code === word.word);
      if (localRef) return identifierHover(localRef, "Local");
      return null;
    },
  });
}

function identifierHover(
  ref: { code: string; name: string; kind?: string; unit?: string; sourceDescription?: string },
  source: string,
): Monaco.languages.Hover {
  const header = `**${ref.name}**`;
  const meta = [ref.kind, ref.unit].filter(Boolean).join(" · ");
  return {
    contents: [
      { value: header },
      { value: `*${source}*${meta ? ` — ${meta}` : ""}` },
      ...(ref.sourceDescription ? [{ value: ref.sourceDescription }] : []),
    ],
  };
}

// ---------------------------------------------------------------------------
// Value hover — the live value of the sub-expression under the cursor. Only
// active when a scope is bound (harvest editors with preview data); otherwise
// returns nothing so just the identifier/function-doc hover shows.
// ---------------------------------------------------------------------------

function registerValueHoverProvider(monaco: typeof Monaco) {
  monaco.languages.registerHoverProvider(LANGUAGE_ID, {
    provideHover(model, position) {
      const scope = getScope(model.uri.toString());
      if (!scope) return null;
      const value = model.getValue();
      if (!value.trim()) return null;

      let ast;
      try {
        ast = parseFormula(value).ast;
      } catch {
        return null;
      }
      const node = findNodeAtOffset(ast, model.getOffsetAt(position));
      if (!node) return null;

      const contents = hoverValueContents(evaluateNode(node, scope));
      if (!contents) return null;

      const span = nodeRange(node);
      return {
        range: span ? toRange(monaco, model, span.start, span.end) : undefined,
        contents,
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Signature help — shows param list when typing function args
// ---------------------------------------------------------------------------

function registerSignatureHelpProvider(monaco: typeof Monaco) {
  monaco.languages.registerSignatureHelpProvider(LANGUAGE_ID, {
    signatureHelpTriggerCharacters: ["(", ","],
    provideSignatureHelp(model, position) {
      const ctx = getContext(model.uri.toString());
      if (!ctx) return null;
      const line = model.getLineContent(position.lineNumber);
      const before = line.slice(0, position.column - 1);
      const openIdx = before.lastIndexOf("(");
      if (openIdx < 0) return null;
      const nameMatch = before.slice(0, openIdx).match(/([a-zA-Z_][a-zA-Z0-9_]*)\s*$/);
      if (!nameMatch) return null;
      // All overloads of the name (e.g. array `last(values)` vs table
      // `last(table)`), shown as separate signatures.
      const overloads = ctx.functions.filter((f) => f.name === nameMatch[1]);
      if (overloads.length === 0) return null;
      const argFragment = before.slice(openIdx + 1);
      const argIndex = argFragment.split(",").length - 1;
      return {
        value: {
          signatures: overloads.map((fn) => ({
            label: `${fn.name}(${fn.parameters.map((p) => p.name).join(", ")}) → ${fn.returnType}`,
            documentation: functionDocumentation(fn),
            parameters: fn.parameters.map((p) => ({
              label: p.name,
              documentation: p.description,
            })),
          })),
          // Prefer the overload that has a parameter at the current position.
          activeSignature: Math.max(
            0,
            overloads.findIndex((fn) => argIndex < fn.parameters.length),
          ),
          activeParameter: argIndex,
        },
        dispose: () => {},
      };
    },
  });
}

function functionDocumentation(fn: {
  description: string;
  examples?: string[];
}): string {
  const parts = [fn.description];
  if (fn.examples?.length) parts.push("Examples:\n" + fn.examples.map((e) => "  " + e).join("\n"));
  return parts.join("\n\n");
}

// ---------------------------------------------------------------------------
// Diagnostics — parse + resolve → Monaco markers
// ---------------------------------------------------------------------------

/** Decoration ids keyed by model URI, so each update can replace its own set. */
const errorDecorationsByUri = new Map<string, string[]>();

/**
 * Computes parse/resolve diagnostics and surfaces them two ways: as Monaco
 * markers (squiggles + hover) and as inline "error lens" decorations that render
 * the message at the end of the offending line — so the error reads where you're
 * typing, not only in a detached footer.
 */
export function setDiagnostics(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  context: FormulaContextSpec | null,
) {
  const value = model.getValue();
  const wholeDoc = (): Monaco.IRange => ({
    startLineNumber: 1,
    startColumn: 1,
    endLineNumber: model.getLineCount(),
    endColumn: model.getLineMaxColumn(model.getLineCount()),
  });

  const errors: { message: string; range: Monaco.IRange }[] = [];

  if (value.trim().length > 0 && context) {
    const result = parseAndResolve(value, context);
    if (!result.ok) {
      if (result.phase === "parse") {
        // An incomplete formula is the normal mid-typing state — don't squiggle
        // or error-lens it. Only a genuinely malformed token gets marked.
        if (result.kind === "malformed") {
          errors.push({
            message: result.message,
            range:
              result.index != null
                ? tokenRangeAtOffset(model, result.index)
                : wholeDoc(),
          });
        }
      } else {
        for (const err of result.errors) {
          errors.push({
            message: err.message,
            range: err.range
              ? toRange(monaco, model, err.range.start, err.range.end)
              : wholeDoc(),
          });
        }
      }
    }
  }

  monaco.editor.setModelMarkers(
    model,
    LANGUAGE_ID,
    errors.map((e) => ({
      severity: monaco.MarkerSeverity.Error,
      message: e.message,
      ...e.range,
    })),
  );

  // One inline message per line (the first error on it).
  const byLine = new Map<number, string>();
  for (const e of errors) {
    if (!byLine.has(e.range.startLineNumber)) {
      byLine.set(e.range.startLineNumber, e.message);
    }
  }
  const decorations: Monaco.editor.IModelDeltaDecoration[] = [...byLine].map(
    ([line, message]) => {
      const col = model.getLineMaxColumn(line);
      return {
        range: new monaco.Range(line, col, line, col),
        options: {
          isWholeLine: true,
          className: "formula-error-line",
          after: {
            content: `   ${message}`,
            inlineClassName: "formula-error-lens",
          },
        },
      };
    },
  );
  const uri = model.uri.toString();
  errorDecorationsByUri.set(
    uri,
    model.deltaDecorations(errorDecorationsByUri.get(uri) ?? [], decorations),
  );
}

/** A squiggle-sized range over the token (or single char) at a source offset. */
function tokenRangeAtOffset(
  model: Monaco.editor.ITextModel,
  offset: number,
): Monaco.IRange {
  const pos = model.getPositionAt(offset);
  const word = model.getWordAtPosition(pos);
  if (word) {
    return {
      startLineNumber: pos.lineNumber,
      startColumn: word.startColumn,
      endLineNumber: pos.lineNumber,
      endColumn: word.endColumn,
    };
  }
  const maxCol = model.getLineMaxColumn(pos.lineNumber);
  return {
    startLineNumber: pos.lineNumber,
    startColumn: pos.column,
    endLineNumber: pos.lineNumber,
    endColumn: Math.min(pos.column + 1, maxCol),
  };
}

function toRange(
  _monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  start: number,
  end: number,
) {
  const startPos = model.getPositionAt(start);
  const endPos = model.getPositionAt(end);
  return {
    startLineNumber: startPos.lineNumber,
    startColumn: startPos.column,
    endLineNumber: endPos.lineNumber,
    endColumn: endPos.column,
  };
}
